import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Mail, MapPin, Music, Star, StarOff, Trophy, X } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { api } from '~/lib/api-client';
import {
  type ArProducerDetail,
  type ArProducerRow,
  type ArRecentBattle,
  type ArWatchlistItem,
  applyForAr,
  arContact,
  arDrops,
  arPick,
  arProducer,
  arProducers,
  arRecentBattles,
  arRising,
  arUnpick,
  arWatchlist,
  arWatchlistActivity,
  arWatchlistAdd,
  arWatchlistRemove,
  createBrief,
  briefs as fetchBriefsList,
} from '~/lib/ar';
import { useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';
import { WaveformPlayer } from '~/ui/WaveformPlayer';
import { Button } from '~/ui/components/button';

// ─── Route search schema ─────────────────────────────────────────────────────

const TAB_IDS = ['producers', 'drops', 'rising', 'briefs', 'recent', 'watchlist'] as const;
type TabId = (typeof TAB_IDS)[number];

const searchSchema = z.object({
  tab: z.enum(TAB_IDS).optional().default('producers'),
  genre: z.string().optional().default(''),
});

export const Route = createFileRoute('/ar/')({
  head: () =>
    buildHead({
      title: 'A&R',
      description: 'Internal A&R dashboard.',
      path: '/ar',
      noindex: true,
    }),
  validateSearch: (s) => searchSchema.parse(s),
  component: ArDashboard,
});

// ─── Genre type (shared with /genres) ────────────────────────────────────────

type Genre = { id: string; slug: string; name: string; kind: 'system' | 'user' };

// ─── Rank medal colours ──────────────────────────────────────────────────────

function rankClass(rank: number | null) {
  if (rank === 1) return 'text-yellow-400';
  if (rank === 2) return 'text-slate-300';
  if (rank === 3) return 'text-orange-400';
  return 'text-muted-foreground';
}

// ─── Watchlist star button ────────────────────────────────────────────────────

function WatchlistToggle({
  producerId,
  inWatchlist,
}: {
  producerId: string;
  inWatchlist: boolean;
}) {
  const qc = useQueryClient();

  const add = useMutation({
    mutationFn: () => arWatchlistAdd(producerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ar', 'watchlist'] });
      qc.invalidateQueries({ queryKey: ['ar', 'producer', producerId] });
    },
  });

  const remove = useMutation({
    mutationFn: () => arWatchlistRemove(producerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ar', 'watchlist'] });
      qc.invalidateQueries({ queryKey: ['ar', 'producer', producerId] });
    },
  });

  const pending = add.isPending || remove.isPending;

  return (
    <button
      type="button"
      disabled={pending}
      onClick={(e) => {
        e.stopPropagation();
        if (inWatchlist) remove.mutate();
        else add.mutate();
      }}
      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-mono uppercase tracking-wider transition-colors ${
        inWatchlist
          ? 'bg-yellow-400/20 text-yellow-400 hover:bg-yellow-400/30'
          : 'bg-muted/40 text-muted-foreground hover:text-foreground'
      } disabled:opacity-40`}
      aria-label={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
    >
      {inWatchlist ? <Star className="size-3.5 fill-current" /> : <StarOff className="size-3.5" />}
    </button>
  );
}

// ─── Producer drawer ──────────────────────────────────────────────────────────

// A&R Pick control: rate a track 1-5, optionally cosign. Fire-and-forget;
// notifies the producer server-side.
function TrackPick({ submissionId }: { submissionId: string }) {
  const [score, setScore] = useState(0);
  const [cosign, setCosign] = useState(false);
  const [saved, setSaved] = useState(false);
  const pick = useMutation({
    mutationFn: (s: number) => arPick(submissionId, s, { cosign }),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    },
  });
  const clear = useMutation({
    mutationFn: () => arUnpick(submissionId),
    onSuccess: () => setScore(0),
  });
  return (
    <div className="mt-2 flex items-center gap-1.5">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">A&R Pick</span>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`${n} star`}
          onClick={() => {
            setScore(n);
            pick.mutate(n);
          }}
          className="text-muted-foreground hover:text-violet-300"
        >
          <Star className={`size-4 ${n <= score ? 'fill-violet-400 text-violet-400' : ''}`} />
        </button>
      ))}
      <label className="ml-2 flex cursor-pointer items-center gap-1 text-[11px] text-muted-foreground">
        <input
          type="checkbox"
          checked={cosign}
          onChange={(e) => setCosign(e.target.checked)}
          className="size-3"
        />
        cosign
      </label>
      {score > 0 && (
        <button
          type="button"
          onClick={() => clear.mutate()}
          className="ml-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          clear
        </button>
      )}
      {saved && <span className="ml-1 text-[11px] text-violet-300">saved</span>}
    </div>
  );
}

// "A label is interested" reach-out, gated server-side by open_to_ar.
function ReachOut({ producerId, openToAr }: { producerId: string; openToAr: boolean }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const contact = useMutation({
    mutationFn: () => arContact(producerId, message),
    onSuccess: () => {
      setSent(true);
      setOpen(false);
      setMessage('');
    },
  });
  if (!openToAr) return <span className="text-[11px] text-muted-foreground">not open to A&R</span>;
  if (sent) return <span className="text-[11px] text-violet-300">request sent</span>;
  return open ? (
    <div className="flex w-full flex-col gap-2">
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Introduce yourself and what you're looking for..."
        rows={3}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!message.trim() || contact.isPending}
          onClick={() => contact.mutate()}
        >
          {contact.isPending ? 'Sending...' : 'Send'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  ) : (
    <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
      <Mail className="mr-1 size-3.5" /> Reach out
    </Button>
  );
}

function ProducerDrawer({
  producerId,
  onClose,
  watchlistIds,
}: {
  producerId: string;
  onClose: () => void;
  watchlistIds: Set<string>;
}) {
  const { data, isLoading } = useQuery<ArProducerDetail>({
    queryKey: ['ar', 'producer', producerId],
    queryFn: () => arProducer(producerId),
    staleTime: 30_000,
  });

  const inWatchlist = watchlistIds.has(producerId);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="presentation"
      />

      {/* Drawer panel */}
      <div className="fixed top-0 right-0 z-50 flex h-full w-full flex-col overflow-y-auto bg-background md:w-[640px] border-l border-border shadow-2xl">
        {/* Header bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border bg-background/95 px-4 py-3 backdrop-blur-sm">
          <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            Producer profile
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        </div>

        {isLoading && (
          <div className="flex flex-1 items-center justify-center p-8">
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        )}

        {data && (
          <div className="flex flex-col gap-6 p-5">
            {/* Identity header */}
            <div className="flex items-start gap-4">
              {data.avatarUrl ? (
                // biome-ignore lint/a11y/useAltText: decorative avatar
                <img
                  src={data.avatarUrl}
                  className="size-16 rounded-full border border-border object-cover"
                />
              ) : (
                <div className="flex size-16 items-center justify-center rounded-full border border-border bg-muted/40 font-mono text-xl text-muted-foreground">
                  {data.handle[0]?.toUpperCase()}
                </div>
              )}

              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold">@{data.handle}</h2>

                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  {data.location && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="size-3" />
                      {data.location}
                    </span>
                  )}
                  {data.openToAr && (
                    <span className="rounded-full bg-emerald-400/15 px-2 py-0.5 font-mono uppercase tracking-wider text-emerald-400 ring-1 ring-emerald-400/30">
                      Open to A&R
                    </span>
                  )}
                  {data.plan === 'paid' && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 font-mono uppercase tracking-wider text-primary ring-1 ring-primary/30">
                      Pro
                    </span>
                  )}
                </div>

                {/* Honor */}
                <p className="mt-1.5 font-mono text-[11px] text-muted-foreground/60 uppercase tracking-wider">
                  Honor: {data.honor}
                </p>

                {/* Reach out to the producer */}
                <div className="mt-2">
                  <ReachOut producerId={data.userId} openToAr={data.openToAr} />
                </div>

                {/* Social links */}
                {Object.keys(data.socialLinks).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Object.entries(data.socialLinks).map(([platform, url]) => (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground underline hover:text-foreground"
                      >
                        {platform}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Bio */}
            {data.bio && <p className="text-sm text-muted-foreground">{data.bio}</p>}

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  ['Matches', data.stats.matchesPlayed],
                  ['Wins', data.stats.wins],
                  ['Score', Math.round(data.stats.totalSubmissionScore)],
                ] as [string, number][]
              ).map(([label, val]) => (
                <div
                  key={label}
                  className="rounded-md border border-border bg-muted/20 p-3 text-center"
                >
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {label}
                  </p>
                  <p className="mt-0.5 text-xl font-bold tabular-nums">{val}</p>
                </div>
              ))}
            </div>

            {/* Top genres */}
            {data.stats.topGenres.length > 0 && (
              <div>
                <SectionLabel>Top genres</SectionLabel>
                <div className="mt-2 flex flex-wrap gap-2">
                  {data.stats.topGenres.map((g) => (
                    <span
                      key={g.slug}
                      className="rounded-full border border-border bg-muted/30 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider"
                    >
                      {g.name} &middot; {Math.round(g.score)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Top tracks */}
            {data.topTracks.length > 0 && (
              <div>
                <SectionLabel icon={<Music className="size-3" />}>Top tracks</SectionLabel>
                <div className="mt-2 flex flex-col gap-3">
                  {data.topTracks.map((t) => (
                    <div
                      key={t.submissionId}
                      className="rounded-md border border-border bg-muted/20 p-3"
                    >
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Trophy className={`size-3.5 ${rankClass(t.finalRank)}`} aria-hidden />
                          <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                            {t.genreName}
                          </span>
                        </div>
                        <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                          {Math.round(t.score)} pts
                        </span>
                      </div>
                      <WaveformPlayer src={t.audioUrl} peaksUrl={t.waveformUrl} height={40} />
                      <TrackPick submissionId={t.submissionId} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Uploaded packs */}
            {data.uploadedPacks.length > 0 && (
              <div>
                <SectionLabel>Uploaded packs</SectionLabel>
                <div className="mt-2 flex flex-col gap-3">
                  {data.uploadedPacks.map((pack) => (
                    <div key={pack.id} className="rounded-md border border-border bg-muted/20 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold">{pack.name}</p>
                          <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                            {pack.genreName} &middot;{' '}
                            {new Date(pack.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        {pack.zipUrl && (
                          <a
                            href={pack.zipUrl}
                            className="font-mono text-[11px] uppercase tracking-wider text-primary hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Download
                          </a>
                        )}
                      </div>
                      {pack.samplesPreview.length > 0 && (
                        <div className="mt-2 flex flex-col gap-2">
                          {pack.samplesPreview.map((s) => (
                            <div key={s.name} className="text-xs">
                              <p className="mb-1 font-mono uppercase tracking-wider text-muted-foreground/70">
                                {s.stemType} - {s.name}
                              </p>
                              <WaveformPlayer src={s.url} height={32} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="sticky bottom-0 flex gap-2 border-t border-border bg-background/95 py-3 backdrop-blur-sm">
              <WatchlistToggle producerId={data.userId} inWatchlist={inWatchlist} />
              {/* Contact via mailto - producer email not yet in API response.
                  V2 hook: surface users.email (or a contact_email field on
                  producer_profiles) on this endpoint for AR users only. */}
              <Button
                variant="outline"
                size="sm"
                className="font-mono text-[11px] uppercase tracking-wider"
                disabled
                title="Contact email not yet surfaced by API - v2"
              >
                <Mail className="mr-1.5 size-3.5" />
                contact
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Producers tab ────────────────────────────────────────────────────────────

function ProducersTab({
  genreSlug,
  watchlistIds,
  onSelectProducer,
}: {
  genreSlug: string;
  watchlistIds: Set<string>;
  onSelectProducer: (id: string) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['ar', 'producers', { genreSlug, limit: 20 }] as const,
    queryFn: () => arProducers({ genreSlug: genreSlug || undefined, limit: 20 }),
    staleTime: 30_000,
  });

  if (isLoading) return <LoadingRow />;

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No producers found for this genre yet.
      </p>
    );
  }

  return (
    <table className="w-full border-separate border-spacing-y-1 text-sm">
      <thead>
        <tr className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <th className="w-8 text-left px-2">#</th>
          <th className="text-left px-2">Producer</th>
          <th className="w-16 text-right px-2">Wins</th>
          <th className="w-20 text-right px-2">Score</th>
          <th className="w-10 text-right px-2" />
        </tr>
      </thead>
      <tbody>
        {items.map((r, i) => (
          <ProducerRow
            key={r.userId}
            row={r}
            rank={i + 1}
            inWatchlist={watchlistIds.has(r.userId)}
            onClick={() => onSelectProducer(r.userId)}
          />
        ))}
      </tbody>
    </table>
  );
}

function ProducerRow({
  row,
  rank,
  inWatchlist,
  onClick,
}: {
  row: ArProducerRow;
  rank: number;
  inWatchlist: boolean;
  onClick: () => void;
}) {
  return (
    <tr
      className="cursor-pointer border border-border hover:bg-muted/30 transition-colors"
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      tabIndex={0}
      aria-label={`View profile for @${row.handle}`}
    >
      <td className="px-2 py-2 tabular-nums text-muted-foreground">{rank}</td>
      <td className="px-2 py-2">
        {row.avatarUrl && (
          // biome-ignore lint/a11y/useAltText: decorative
          <img src={row.avatarUrl} className="mr-2 inline-block size-5 rounded-full" />
        )}
        <span className="font-mono text-[12px]">@{row.handle}</span>
      </td>
      <td className="px-2 py-2 text-right tabular-nums">{row.wins}</td>
      <td className="px-2 py-2 text-right tabular-nums font-semibold">
        {Math.round(row.totalSubmissionScore)}
      </td>
      <td className="px-2 py-2 text-right">
        <WatchlistToggle producerId={row.userId} inWatchlist={inWatchlist} />
      </td>
    </tr>
  );
}

// ─── Recent battles tab ───────────────────────────────────────────────────────

function RecentTab({ genreSlug }: { genreSlug: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['ar', 'recent', { genreSlug, limit: 20 }] as const,
    queryFn: () => arRecentBattles({ genreSlug: genreSlug || undefined, limit: 20 }),
    staleTime: 30_000,
  });

  if (isLoading) return <LoadingRow />;

  const items = (data?.items ?? []) as ArRecentBattle[];

  if (items.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No recent battles for this genre yet.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {items.map((battle) => (
        <div key={battle.matchId} className="rounded-md border border-border bg-muted/10 p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="rounded-full border border-border bg-muted/30 px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-wider">
              {battle.genreName}
            </span>
            <span className="font-mono text-[11px] text-muted-foreground">
              {new Date(battle.endedAt).toLocaleDateString()}
            </span>
          </div>
          <div className="flex flex-col gap-3">
            {battle.submissions.map((s) => (
              <div key={s.submissionId}>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5">
                    <Trophy className={`size-3 ${rankClass(s.finalRank)}`} aria-hidden />
                    <span className="font-mono text-[11px] text-muted-foreground">
                      @{s.userHandle}
                    </span>
                  </span>
                  <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                    {Math.round(s.score)} pts
                  </span>
                </div>
                <WaveformPlayer
                  src={s.audioUrl}
                  peaksUrl={(s as { waveformUrl?: string | null }).waveformUrl}
                  height={40}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Watchlist tab ────────────────────────────────────────────────────────────

function WatchlistTab({
  onSelectProducer,
}: {
  onSelectProducer: (id: string) => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['ar', 'watchlist'] as const,
    queryFn: arWatchlist,
    staleTime: 10_000,
  });

  const remove = useMutation({
    mutationFn: (id: string) => arWatchlistRemove(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['ar', 'watchlist'] });
      qc.invalidateQueries({ queryKey: ['ar', 'producer', id] });
    },
  });

  if (isLoading) return <LoadingRow />;

  const items = (data?.items ?? []) as ArWatchlistItem[];

  if (items.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Your watchlist is empty. Star a producer from the Top Producers tab.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => (
        <div
          key={item.producerId}
          className="flex cursor-pointer items-center gap-3 rounded-md border border-border p-3 hover:bg-muted/20 transition-colors"
          onClick={() => onSelectProducer(item.producerId)}
          onKeyDown={(e) => e.key === 'Enter' && onSelectProducer(item.producerId)}
          aria-label={`View profile for @${item.handle}`}
        >
          {item.avatarUrl ? (
            // biome-ignore lint/a11y/useAltText: decorative
            <img src={item.avatarUrl} className="size-9 rounded-full border border-border" />
          ) : (
            <div className="flex size-9 items-center justify-center rounded-full border border-border bg-muted/40 font-mono text-sm text-muted-foreground">
              {item.handle[0]?.toUpperCase()}
            </div>
          )}

          <div className="flex-1 min-w-0">
            <p className="font-mono text-[12px]">@{item.handle}</p>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {item.wins} wins &middot; {Math.round(item.totalSubmissionScore)} score
            </p>
            {item.note && (
              <p className="mt-0.5 text-xs text-muted-foreground/70 truncate">{item.note}</p>
            )}
          </div>

          <button
            type="button"
            disabled={remove.isPending}
            onClick={(e) => {
              e.stopPropagation();
              remove.mutate(item.producerId);
            }}
            className="rounded-md p-1.5 text-muted-foreground/50 hover:text-destructive disabled:opacity-40"
            aria-label="Remove from watchlist"
          >
            <X className="size-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function SectionLabel({
  children,
  icon,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
      {icon}
      {children}
    </div>
  );
}

function LoadingRow() {
  return <p className="py-8 text-center text-sm text-muted-foreground">Loading...</p>;
}

// ─── Main dashboard ───────────────────────────────────────────────────────────

// Fresh standout tracks to scout, with inline A&R Pick.
function DropsTab({ genreSlug }: { genreSlug: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['ar', 'drops', { genreSlug }] as const,
    queryFn: () => arDrops({ genreSlug: genreSlug || undefined, limit: 30 }),
  });
  const items = data?.items ?? [];
  if (isLoading) return <LoadingRow />;
  if (items.length === 0)
    return <p className="text-sm text-muted-foreground">No fresh drops to scout.</p>;
  return (
    <div className="flex flex-col gap-3">
      {items.map((d) => (
        <div key={d.submissionId} className="rounded-md border border-border bg-muted/20 p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-sm font-semibold">
              {d.title ?? 'Untitled'}{' '}
              <span className="font-normal text-muted-foreground">· @{d.producer.handle}</span>
            </span>
            <span className="font-mono text-[11px] text-muted-foreground">
              {d.genre.name} · {d.arPickCount} A&R
            </span>
          </div>
          <WaveformPlayer src={d.audioUrl} height={40} />
          <TrackPick submissionId={d.submissionId} />
        </div>
      ))}
    </div>
  );
}

// Producers with recent momentum + watchlist activity.
function RisingTab({ onSelectProducer }: { onSelectProducer: (id: string) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['ar', 'rising'] as const,
    queryFn: () => arRising(20),
  });
  const { data: activity } = useQuery({
    queryKey: ['ar', 'watch-activity'] as const,
    queryFn: arWatchlistActivity,
  });
  const items = data?.items ?? [];
  if (isLoading) return <LoadingRow />;
  return (
    <div className="space-y-5">
      <div>
        <SectionLabel icon={<Trophy className="size-3" />}>Rising (last 14 days)</SectionLabel>
        <div className="mt-2 flex flex-col gap-1">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No risers yet.</p>
          ) : (
            items.map((r) => (
              <button
                key={r.producerId}
                type="button"
                onClick={() => onSelectProducer(r.producerId)}
                className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-left hover:border-primary"
              >
                <span className="text-sm font-medium">@{r.handle}</span>
                <span className="font-mono text-[11px] text-muted-foreground">
                  {r.recentWins}W · {Math.round(r.recentScore)} pts · {r.recentMatches} matches
                </span>
              </button>
            ))
          )}
        </div>
      </div>
      {(activity?.items.length ?? 0) > 0 && (
        <div>
          <SectionLabel icon={<Music className="size-3" />}>Watchlist activity</SectionLabel>
          <div className="mt-2 flex flex-col gap-1">
            {activity?.items.map((a) => (
              <div
                key={a.submissionId}
                className="rounded-md border border-border px-3 py-2 text-sm"
              >
                <span className="font-medium">@{a.handle}</span>{' '}
                <span className="text-muted-foreground">
                  dropped {a.title ?? 'a track'} · {a.genreName}
                  {a.finalRank === 1 ? ' · won' : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Post + browse A&R briefs.
function BriefsTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['ar', 'briefs-list'] as const, queryFn: fetchBriefsList });
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reward, setReward] = useState('');
  const [deadline, setDeadline] = useState('');
  const create = useMutation({
    mutationFn: () =>
      createBrief({
        title,
        description,
        reward: reward || undefined,
        deadline: deadline || undefined,
      }),
    onSuccess: () => {
      setTitle('');
      setDescription('');
      setReward('');
      setDeadline('');
      qc.invalidateQueries({ queryKey: ['ar', 'briefs-list'] });
    },
  });
  const items = data?.items ?? [];
  return (
    <div className="space-y-5">
      <div className="space-y-2 rounded-md border border-border p-3">
        <SectionLabel icon={<Mail className="size-3" />}>Post a brief</SectionLabel>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (e.g. Dark trap, 140bpm)"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What you're looking for..."
          rows={3}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <div className="flex gap-2">
          <input
            value={reward}
            onChange={(e) => setReward(e.target.value)}
            placeholder="Reward (optional)"
            className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </div>
        <Button
          size="sm"
          disabled={!title || !description || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'Posting...' : 'Post brief'}
        </Button>
      </div>
      <div>
        <SectionLabel icon={<Trophy className="size-3" />}>Open briefs</SectionLabel>
        <div className="mt-2 flex flex-col gap-1">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No open briefs.</p>
          ) : (
            items.map((b) => (
              <a
                key={b.id}
                href="/briefs"
                className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm hover:border-primary"
              >
                <span className="font-medium">{b.title}</span>
                <span className="font-mono text-[11px] text-muted-foreground">
                  {b.entries} entries · by @{b.arHandle}
                </span>
              </a>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Shown to non-A&R users visiting /ar: a verified-A&R application form.
function ApplyForAr({ signedIn }: { signedIn: boolean }) {
  const [labelName, setLabelName] = useState('');
  const [evidence, setEvidence] = useState('');
  const apply = useMutation({ mutationFn: () => applyForAr(labelName, evidence) });
  return (
    <section className="mx-auto max-w-xl px-4 py-16 space-y-5">
      <header className="space-y-1 text-center">
        <h2 className="text-2xl font-bold">Become a verified A&R</h2>
        <p className="text-sm text-muted-foreground">
          Scouts get a dashboard to discover, pick, and reach out to producers. Approval is manual.
        </p>
      </header>
      {!signedIn ? (
        <p className="text-center text-sm text-muted-foreground">Sign in to apply.</p>
      ) : apply.isSuccess ? (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-6 text-center text-sm">
          Application submitted. We'll review it and email you.
        </div>
      ) : (
        <div className="space-y-3 rounded-lg border border-border p-4">
          <input
            value={labelName}
            onChange={(e) => setLabelName(e.target.value)}
            placeholder="Label / company name"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <textarea
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            placeholder="Tell us who you are and what you scout (links help)."
            rows={5}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <Button
            disabled={!labelName || !evidence || apply.isPending}
            onClick={() => apply.mutate()}
          >
            {apply.isPending ? 'Submitting...' : 'Apply'}
          </Button>
          {apply.isError && <p className="text-sm text-red-500">Could not submit. Try again.</p>}
        </div>
      )}
    </section>
  );
}

function ArDashboard() {
  const { data: session } = useSession();
  const navigate = useNavigate({ from: '/ar/' });
  const { tab, genre } = useSearch({ from: '/ar/' });

  const [selectedProducerId, setSelectedProducerId] = useState<string | null>(null);

  // Fetch genres for the filter
  const { data: genresData } = useQuery({
    queryKey: ['genres'] as const,
    queryFn: () => api<{ items: Genre[] }>('/genres'),
    staleTime: 300_000,
  });
  const genres = genresData?.items ?? [];

  // Fetch watchlist for the star state across tabs
  const { data: watchlistData } = useQuery({
    queryKey: ['ar', 'watchlist'] as const,
    queryFn: arWatchlist,
    staleTime: 10_000,
    enabled: session?.role === 'ar' || session?.role === 'admin',
  });
  const watchlistIds = new Set<string>((watchlistData?.items ?? []).map((w) => w.producerId));

  if (session?.role !== 'ar' && session?.role !== 'admin') {
    return <ApplyForAr signedIn={Boolean(session)} />;
  }

  function setTab(t: TabId) {
    navigate({ search: (s) => ({ ...s, tab: t }) });
  }

  function setGenre(g: string) {
    navigate({ search: (s) => ({ ...s, genre: g }) });
  }

  return (
    <section className="mx-auto max-w-5xl px-4 py-8 space-y-5">
      {/* Page header */}
      <header>
        <h2 className="text-2xl font-bold">A&R Dashboard</h2>
        <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
          Top producers per genre &middot; listen &middot; watchlist
        </p>
      </header>

      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Genre filter */}
        <select
          value={genre}
          onChange={(e) => setGenre(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
        >
          <option value="">All genres</option>
          {genres.map((g) => (
            <option key={g.id} value={g.slug}>
              {g.name}
            </option>
          ))}
        </select>

        {/* Tabs */}
        <div className="flex rounded-md border border-border overflow-hidden">
          {(
            [
              { id: 'producers', label: 'Top Producers' },
              { id: 'drops', label: 'Drops' },
              { id: 'rising', label: 'Rising' },
              { id: 'briefs', label: 'Briefs' },
              { id: 'recent', label: 'Recent' },
              { id: 'watchlist', label: 'Watchlist' },
            ] as { id: TabId; label: string }[]
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider transition-colors ${
                tab === t.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
              {t.id === 'watchlist' && watchlistIds.size > 0 && (
                <span className="ml-1.5 rounded-full bg-white/20 px-1.5 text-[10px]">
                  {watchlistIds.size}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div>
        {tab === 'producers' && (
          <ProducersTab
            genreSlug={genre}
            watchlistIds={watchlistIds}
            onSelectProducer={setSelectedProducerId}
          />
        )}
        {tab === 'drops' && <DropsTab genreSlug={genre} />}
        {tab === 'rising' && <RisingTab onSelectProducer={setSelectedProducerId} />}
        {tab === 'briefs' && <BriefsTab />}
        {tab === 'recent' && <RecentTab genreSlug={genre} />}
        {tab === 'watchlist' && <WatchlistTab onSelectProducer={setSelectedProducerId} />}
      </div>

      {/* Producer drawer */}
      {selectedProducerId && (
        <ProducerDrawer
          producerId={selectedProducerId}
          onClose={() => setSelectedProducerId(null)}
          watchlistIds={watchlistIds}
        />
      )}
    </section>
  );
}
